import * as Yup from 'yup';
import { Pandadoc, AdobesignText, SpotdraftLogo } from '@/assets/icons/ats';
import {
  AuthType,
  type ProviderConfig,
  type SpotDraftRegion,
} from '@/types/esignature';

export const ESIGNATURE_PROVIDERS = {
  DOCUSIGN: 'docusign/docusign_api',
  PANDADOC: 'pandadocs/pandadocs_api',
  ADOBE: 'adobe/adobe_api',
  SPOTDRAFT: 'spotdraft/spotdraft_api',
} as const;

export const SPOTDRAFT_DEFAULT_REGION: SpotDraftRegion = 'us';

export const SPOTDRAFT_REGION_OPTIONS: {
  label: string;
  value: SpotDraftRegion;
}[] = [
  { label: 'US', value: 'us' },
  { label: 'India', value: 'in' },
  { label: 'EU', value: 'eu' },
  { label: 'Middle East', value: 'me' },
];

export const getESignatureProvidersConfig = (): Record<
  string,
  ProviderConfig
> => {
  const config: Record<string, ProviderConfig> = {
    [ESIGNATURE_PROVIDERS.DOCUSIGN]: {
      id: ESIGNATURE_PROVIDERS.DOCUSIGN,
      label: 'Docusign',
      description:
        'Send offers to candidates and receive signatures with Docusign',
      img: 'https://assets.kula.ai/images/static/core-web/job-boards/docusign.svg',
      authType: AuthType.OAUTH,
      integrationType: 'docusign',
      oauthConfig: {
        formMethod: 'POST',
        formUrl: '/api/v1/account_integrations/auth/docusign',
      },
    },
    [ESIGNATURE_PROVIDERS.PANDADOC]: {
      id: ESIGNATURE_PROVIDERS.PANDADOC,
      label: 'PandaDoc',
      description:
        'Send offers to candidates and receive signatures with PandaDoc',
      descriptionForm:
        'Send offer documents and collect signatures from candidates right in Kula using the PandaDoc integration',
      note: 'To integrate PandaDoc with Kula, you need a paid PandaDoc account. Get your API key from the Dev Center under Production configuration → API keys.',
      icon: Pandadoc,
      iconProps: { width: '103px', height: '24px' },
      authType: AuthType.API_KEY,
      integrationType: 'pandadocs',
      apiKeyConfig: {
        endpoint: '/api/v1/account_integrations',
        fieldLabel: 'PandaDoc API Key',
        fieldPlaceholder: 'Enter your PandaDoc API key',
      },
    },
  };

  config[ESIGNATURE_PROVIDERS.ADOBE] = {
    id: ESIGNATURE_PROVIDERS.ADOBE,
    label: 'Adobe Sign',
    description: 'Send offers to candidates and receive signatures with Adobe',
    descriptionForm:
      'Send offer documents and collect signatures from candidates right in Kula using the Adobe Sign integration',
    note: 'To integrate Adobe Sign with Kula, you need your OAuth client credentials. Get your Client ID and Client Secret from the Adobe Developer Console.',
    icon: AdobesignText,
    iconProps: { width: '110px', height: '24px' },
    authType: AuthType.OAUTH_WITH_CREDENTIALS,
    integrationType: 'adobe',
    credentialsConfig: {
      endpoint: '/api/v1/account_integrations/connect_adobe_oauth_key',
      fields: [
        {
          name: 'client_id',
          label: 'Client ID',
          placeholder: 'Enter your Adobe Sign Client ID',
          validationSchema: Yup.string().required('Client ID is required'),
        },
        {
          name: 'client_secret',
          label: 'Client Secret',
          placeholder: 'Enter your Adobe Sign Client Secret',
          validationSchema: Yup.string().required('Client Secret is required'),
        },
      ],
      oauthConfig: {
        formMethod: 'POST',
        formUrl: '/api/v1/account_integrations/auth/adobe',
      },
    },
  };

  config[ESIGNATURE_PROVIDERS.SPOTDRAFT] = {
    id: ESIGNATURE_PROVIDERS.SPOTDRAFT,
    label: 'SpotDraft',
    description:
      'Send offers to candidates and receive signatures with SpotDraft',
    descriptionForm:
      'Send offer documents and collect signatures from candidates right in Kula using the SpotDraft integration',
    note: 'Get your Client ID and Client Secret from SpotDraft under Settings → Developer Settings.',
    icon: SpotdraftLogo,
    iconProps: { width: '123px', height: '24px' },
    authType: AuthType.CREDENTIALS_WITH_LOOKUP,
    integrationType: 'spotdraft',
  };

  return config;
};

export const PANDADOC_API_KEY_VALIDATION = {
  length: 40,
  pattern: /^[0-9A-Fa-f]+$/,
  schema: Yup.string()
    .required('API key is required')
    .length(40, 'API key must be exactly 40 characters long')
    .matches(
      /^[0-9A-Fa-f]+$/,
      'API key must contain only hexadecimal characters (0-9, A-F)'
    ),
};

export const ERROR_MESSAGES = {
  NO_AUTH_TOKEN: 'No authentication token found',
  PANDADOC_CONNECTION_FAILED:
    'PandaDoc integration failed. Verify your API key and try again',
  CREDENTIALS_VERIFICATION_FAILED:
    'integration failed. Verify your credentials and try again',
  CONNECTION_FAILED: 'Failed to connect',
  CREDENTIALS_SAVE_FAILED: 'Failed to save credentials',
  SPOTDRAFT_LOOKUP_FAILED:
    "Couldn't validate credentials. Check your Client ID, Client Secret, and Region, then try again.",
};

export const SUCCESS_MESSAGES = {
  CONNECTED: 'integration has been successfully connected.',
  CONNECTION_SUCCESS: '',
  CREDENTIALS_SAVED:
    'Credentials saved successfully. Redirecting to Adobe Sign to finish the integration.',
};

// E-sign Edit constants
export const INTEGRATION_TYPES = {
  DOCUSIGN: 'docusign',
  PANDADOC: 'pandadocs',
  ADOBE: 'adobe',
  SPOTDRAFT: 'spotdraft',
} as const;

export const RADIO_OPTIONS = {
  YES_NO: [
    { label: 'Yes', value: '1' },
    { label: 'No', value: '0' },
  ],
  SIGNING_ORDER: [
    { label: 'Employer first', value: '1' },
    { label: 'Candidate first', value: '0' },
  ],
} as const;

export const TEMPLATE_VARIABLES_VIEW = [
  'org',
  'candidate',
  'job',
  'sender',
  'offer',
];

export const ESIGN_EDITOR_PLUGINS = {
  docusign: ['link', 'image'], // Full editor with link and image plugins
  pandadocs: [], // Basic editor without link/image plugins
  adobe: [],
  spotdraft: [],
} as const;

export const USE_TEXT_EDITOR = {
  docusign: true,
  pandadocs: true,
  adobe: false,
  spotdraft: true,
} as const;

export const ESIGN_SUBJECT_MAX_LENGTH = {
  docusign: 100,
  pandadocs: 150,
  adobe: 150,
  spotdraft: 150,
} as const;

export const getIntegrationTypeParam = (
  integrationType: string
): string | undefined => {
  if (integrationType === INTEGRATION_TYPES.PANDADOC) {
    return `${integrationType}/pandadocs_api`;
  }
  if (integrationType === INTEGRATION_TYPES.ADOBE) {
    return `${integrationType}/adobe_api`;
  }
  if (integrationType === INTEGRATION_TYPES.DOCUSIGN) {
    return `${integrationType}/docusign_api`;
  }
  if (integrationType === INTEGRATION_TYPES.SPOTDRAFT) {
    return `${integrationType}/spotdraft_api`;
  }
  return undefined;
};

export const getCollapseSectionProps = () => ({
  headerConfig: {
    containerStyles: () => ({
      backgroundColor: 'ats-tertiary',
      p: '15px',
    }),
    textStyles: {
      color: 'ats-primary',
    },
    iconStyles: {
      width: '24px',
      height: '24px',
    },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
    m: '0px -24px',
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1em 2em',
    backgroundColor: 'white',
  },
});

// Offer e-sign composer section headings (SectionWithHeading titles).
export const ESIGN_SECTION_TITLES = {
  DETAILS: 'E-sign details',
  MESSAGE: 'Message',
  EMAIL: 'Email',
  CANDIDATE_EMAIL: 'Candidate email',
  COMPANY_EMAIL: 'Company signer email',
  CARBON_COPY_EMAIL: 'Copy recipient email',
} as const;

// Per-recipient toggle — OFF = one email to everyone, ON = a subject/body per recipient.
export const ESIGN_CUSTOM_MESSAGE_TOGGLE = {
  LABEL: 'Custom email for each recipient',
  HINT: 'Send a tailored message to the candidate, company signer, and offer copy recipients',
} as const;

// DocuSign CC (CarbonCopy) recipient picker. BCC deferred — platform-disabled (444).
export const ESIGN_RECIPIENT_FIELDS = {
  CC: {
    LABEL: 'Choose who gets a copy of the signed offer',
    PLACEHOLDER: 'Add recipients',
  },
} as const;

export const ESIGN_MESSAGES = {
  UPDATE_ERROR: {
    DOCUSIGN: 'Error in updating Docusign settings',
    PANDADOC: 'Error in updating PandaDoc settings',
    ADOBE: 'Error in updating Adobe Sign settings',
  },
  UPDATE_SUCCESS: {
    DOCUSIGN: 'Docusign settings updated',
    PANDADOC: 'PandaDoc settings updated',
    ADOBE: 'Adobe Sign settings updated',
  },
  HEADER: {
    DOCUSIGN_ADMIN: 'Docusign Offer Settings',
    PANDADOC_ADMIN: 'PandaDoc Settings',
    ADOBE_ADMIN: 'Adobe Settings',
    NON_ADMIN: 'Signature Flow',
  },
  SEND_ERROR: 'Unable to send offer',
  SEND_SUCCESS: 'Offer sent',
  SUBJECT_REQUIRED: 'Subject is required',
  PREVIEW_ERROR: 'Unable to open the DocuSign preview, please try again',
  COMPANY_SUBJECT_REQUIRED: 'Company signer email subject is required',
  CARBON_COPY_SUBJECT_REQUIRED: 'Copy recipient email subject is required',
  COMPANY_INVALID_VARIABLES:
    'Remove the empty variables from the company signer email to continue',
  CARBON_COPY_INVALID_VARIABLES:
    'Remove the empty variables from the copy recipient email to continue',
};
